/**
 * หน้าบันทึกรายการ — ทีละขั้นตอน
 *
 *   1 ทะเบียนลูกค้า -> 2 พิกัด GPS -> 3 ถ่ายภาพ -> 4 ตรวจสอบ -> 5 ส่ง
 *
 * ขั้นที่ทำเสร็จแล้วจะยุบเป็นแถบสรุป ต่อกันลงมาเหมือนใบบันทึกที่ค่อย ๆ พิมพ์ออกมา
 * ผู้ใช้เห็นตลอดว่าทำอะไรไปแล้วโดยไม่ต้องย้อนกลับ
 *
 * การส่งข้อมูล "ผ่านคิวเสมอ" ไม่ว่าออนไลน์อยู่หรือไม่
 * ทำให้เส้นทางโค้ดมีชุดเดียว และรายการไม่มีทางหายแม้เน็ตหลุดกลางคัน
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { SessionResponse } from '../lib/api';
import { useGeolocation, useIsPositionFresh } from '../lib/gps';
import { GpsPanel } from '../components/GpsPanel';
import { acceptAttribute, formatBytes, processImage, sizeAdvice, ImageError } from '../lib/image';
import type { ProcessedImage } from '../lib/image';
import { checkRegistration } from '../../shared/registration';
import { meetsThreshold } from '../../shared/gps';
import { ulid } from '../../shared/ids';
import { putQueued, requestPersistentStorage } from '../lib/db';
import { syncQueue } from '../lib/queue';
import { metres } from '../lib/format';

type Step = 'registration' | 'gps' | 'photo' | 'review';

const STEP_ORDER: Step[] = ['registration', 'gps', 'photo', 'review'];
const STEP_TITLES: Record<Step, string> = {
  registration: 'เลขทะเบียนลูกค้า',
  gps: 'พิกัด GPS',
  photo: 'ภาพหน้างาน',
  review: 'ตรวจสอบก่อนส่ง',
};

export function CaptureRoute({ session }: { session: SessionResponse }) {
  const navigate = useNavigate();
  const {
    registration: registrationSettings,
    gps: gpsSettings,
    image: imageSettings,
  } = session.settings;

  const [step, setStep] = useState<Step>('registration');
  const [registration, setRegistration] = useState('');
  const [registrationTouched, setRegistrationTouched] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  const [note, setNote] = useState('');
  const [photo, setPhoto] = useState<ProcessedImage | null>(null);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoError, setPhotoError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const gps = useGeolocation(gpsSettings);
  const { fresh, ageSeconds } = useIsPositionFresh(gps.best, gpsSettings.maxPositionAgeSeconds);
  const fileInput = useRef<HTMLInputElement>(null);

  const registrationCheck = checkRegistration(registration, registrationSettings);
  const accuracyOk = gps.best ? meetsThreshold(gps.best.accuracyM, gpsSettings.thresholdM) : false;
  const needsOverride = gps.best !== null && !accuracyOk;
  const overrideLongEnough = overrideReason.trim().length >= gpsSettings.overrideReasonMinLength;

  // ขอให้เบราว์เซอร์ไม่ลบข้อมูลของเราตอนพื้นที่ไม่พอ ทำครั้งเดียวตอนเริ่มบันทึกจริง
  useEffect(() => {
    void requestPersistentStorage();
  }, []);

  // เก็บ object URL ของ preview ไม่ให้รั่วเมื่อถ่ายใหม่หรือออกจากหน้า
  useEffect(() => {
    return () => {
      if (photo) URL.revokeObjectURL(photo.previewUrl);
    };
  }, [photo]);

  const gpsReady =
    gps.best !== null && fresh && (accuracyOk || (needsOverride && overrideLongEnough));

  const handleFile = useCallback(
    async (file: File | undefined) => {
      if (!file) return;
      setPhotoBusy(true);
      setPhotoError(null);
      try {
        const processed = await processImage(file, imageSettings);
        setPhoto((previous) => {
          if (previous) URL.revokeObjectURL(previous.previewUrl);
          return processed;
        });
      } catch (error) {
        setPhotoError(
          error instanceof ImageError
            ? error.message
            : 'ประมวลผลรูปไม่สำเร็จ กรุณาถ่ายใหม่อีกครั้ง',
        );
      } finally {
        setPhotoBusy(false);
        // เคลียร์ค่า input เพื่อให้เลือกไฟล์เดิมซ้ำได้
        if (fileInput.current) fileInput.current.value = '';
      }
    },
    [imageSettings],
  );

  const submit = useCallback(async () => {
    if (!gps.best || !photo || submitting) return;
    setSubmitting(true);

    const id = `q${ulid()}`;
    try {
      await putQueued({
        id,
        status: 'pending',
        customerRegistration: registrationCheck.raw,
        gps: {
          latitude: gps.best.latitude,
          longitude: gps.best.longitude,
          accuracyM: gps.best.accuracyM,
          altitude: gps.best.altitude,
          heading: gps.best.heading,
          speed: gps.best.speed,
          positionTimestamp: gps.best.positionTimestamp,
        },
        ...(note.trim() ? { note: note.trim() } : {}),
        ...(needsOverride ? { gpsOverrideReason: overrideReason.trim() } : {}),
        imageBlob: photo.blob,
        imageContentType: photo.contentType,
        imageSizeBytes: photo.sizeBytes,
        imageSha256: photo.sha256,
        captureId: null,
        uploadToken: null,
        uploadTokenExpiresAt: null,
        uploaded: false,
        awaitingGpsApproval: needsOverride,
        attempts: 0,
        nextAttemptAt: 0,
        lastErrorCode: null,
        lastErrorMessage: null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });

      // ไม่ await ผลการส่ง — ผู้ใช้ไปหน้าถัดไปได้เลย คิวจะส่งต่อให้เอง
      void syncQueue();
      navigate(`/result/${id}`, { replace: true });
    } catch {
      setSubmitting(false);
      setPhotoError('บันทึกลงเครื่องไม่สำเร็จ พื้นที่จัดเก็บอาจเต็ม กรุณาส่งรายการในคิวให้หมดก่อน');
    }
  }, [
    gps.best,
    photo,
    submitting,
    registrationCheck.raw,
    note,
    needsOverride,
    overrideReason,
    navigate,
  ]);

  const stepIndex = STEP_ORDER.indexOf(step);

  return (
    <div className="wrap stack-lg">
      <div className="stack" style={{ gap: 'var(--space-2)' }}>
        <div className="spread">
          <button type="button" className="btn-quiet" onClick={() => navigate('/my')}>
            ‹ ยกเลิก
          </button>
          <span className="small muted">
            ขั้นที่ {stepIndex + 1} จาก {STEP_ORDER.length}
          </span>
        </div>
        <div
          className="steps"
          role="progressbar"
          aria-valuenow={stepIndex + 1}
          aria-valuemin={1}
          aria-valuemax={STEP_ORDER.length}
          aria-label="ความคืบหน้า"
        >
          {STEP_ORDER.map((name, index) => (
            <i key={name} className={index < stepIndex ? 'on' : index === stepIndex ? 'now' : ''} />
          ))}
        </div>
      </div>

      {/* ---- แถบสรุปของขั้นที่ทำเสร็จแล้ว ---- */}
      {stepIndex > 0 && (
        <div className="stack" style={{ gap: 'var(--space-2)' }}>
          <button type="button" className="stub" onClick={() => setStep('registration')}>
            <span className="tick" aria-hidden="true">
              ✓
            </span>
            เลขทะเบียนลูกค้า
            <b>{registrationCheck.normalized}</b>
          </button>
          {stepIndex > 1 && gps.best && (
            <button type="button" className="stub" onClick={() => setStep('gps')}>
              <span className="tick" aria-hidden="true">
                ✓
              </span>
              พิกัด GPS
              <b>±{metres(gps.best.accuracyM)}</b>
            </button>
          )}
          {stepIndex > 2 && photo && (
            <button type="button" className="stub" onClick={() => setStep('photo')}>
              <span className="tick" aria-hidden="true">
                ✓
              </span>
              ภาพหน้างาน
              <b>{formatBytes(photo.sizeBytes)}</b>
            </button>
          )}
        </div>
      )}

      {/* ---- ขั้นที่ 1: เลขทะเบียน ---- */}
      {step === 'registration' && (
        <div className="card stack">
          <div>
            <h1 className="card-title">{STEP_TITLES.registration}</h1>
            <p className="card-sub">กรอกได้ทั้งแบบมีขีดและไม่มีขีด ระบบจะจัดรูปแบบให้เอง</p>
          </div>
          <div className="perf" />
          <div className="field">
            <label className="label" htmlFor="registration">
              เลขทะเบียนลูกค้า
            </label>
            <input
              id="registration"
              className="input mono"
              inputMode="text"
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              value={registration}
              onChange={(event) => setRegistration(event.target.value)}
              onBlur={() => setRegistrationTouched(true)}
              aria-invalid={registrationTouched && !registrationCheck.valid}
              aria-describedby="registration-hint"
            />
            <p className="hint" id="registration-hint">
              {registrationCheck.normalized
                ? `จะบันทึกเป็น: ${registrationCheck.normalized}`
                : `ยาว ${registrationSettings.minLength}–${registrationSettings.maxLength} ตัวอักษร`}
            </p>
            {registrationTouched && registrationCheck.reason && (
              <p className="error-text" role="alert">
                {registrationCheck.reason}
              </p>
            )}
          </div>
        </div>
      )}

      {/* ---- ขั้นที่ 2: GPS ---- */}
      {step === 'gps' && (
        <div className="card stack">
          <div>
            <h1 className="card-title">{STEP_TITLES.gps}</h1>
            <p className="card-sub">
              ยืนกลางที่โล่ง เลี่ยงใต้ชายคาและระหว่างอาคารสูง เพื่อให้สัญญาณดาวเทียมชัดที่สุด
            </p>
          </div>
          <div className="perf" />

          <GpsPanel
            phase={gps.phase}
            reading={gps.best}
            settings={gpsSettings}
            secondsLeft={gps.secondsLeft}
            samples={gps.samples}
            errorMessage={gps.errorMessage}
            ageSeconds={ageSeconds}
            fresh={fresh}
          />

          {needsOverride && gpsSettings.allowSupervisorOverride && (
            <div className="field">
              <label className="label" htmlFor="override-reason">
                เหตุผลที่ขออนุมัติจากหัวหน้างาน
              </label>
              <textarea
                id="override-reason"
                className="input"
                value={overrideReason}
                onChange={(event) => setOverrideReason(event.target.value)}
                placeholder="เช่น จุดติดตั้งอยู่ใต้สะพานคอนกรีต สัญญาณดาวเทียมถูกบัง"
                aria-describedby="override-hint"
              />
              <p className="hint" id="override-hint">
                ระบบจะบันทึกค่าความแม่นยำจริง {gps.best ? metres(gps.best.accuracyM) : ''}{' '}
                ไว้ตามที่วัดได้ ไม่มีการปรับแต่งพิกัด · ต้องยาวอย่างน้อย{' '}
                {gpsSettings.overrideReasonMinLength} ตัวอักษร
              </p>
            </div>
          )}

          {needsOverride && !gpsSettings.allowSupervisorOverride && (
            <div className="banner banner-bad" role="alert">
              <span>ระบบไม่อนุญาตให้บันทึกเมื่อพิกัดไม่ผ่านเกณฑ์ กรุณาย้ายตำแหน่งแล้ววัดใหม่</span>
            </div>
          )}
        </div>
      )}

      {/* ---- ขั้นที่ 3: ภาพ ---- */}
      {step === 'photo' && (
        <div className="card stack">
          <div>
            <h1 className="card-title">{STEP_TITLES.photo}</h1>
            <p className="card-sub">ถ่ายให้เห็นจุดติดตั้งชัดเจน เลี่ยงถ่ายย้อนแสง</p>
          </div>
          <div className="perf" />

          <input
            ref={fileInput}
            type="file"
            accept={acceptAttribute(imageSettings)}
            // capture="environment" เปิดกล้องหลังเป็นค่าเริ่มต้นบน Android และ iOS
            // เบราว์เซอร์ที่ไม่รองรับจะกลายเป็นตัวเลือกไฟล์ธรรมดาโดยอัตโนมัติ
            capture="environment"
            className="sr-only"
            id="photo-input"
            onChange={(event) => void handleFile(event.target.files?.[0])}
          />

          {photo ? (
            <>
              <div className="photo-frame">
                <img src={photo.previewUrl} alt="ตัวอย่างภาพที่ถ่าย" />
              </div>
              <div className="photo-meta">
                <span>{photo.contentType === 'image/webp' ? 'WEBP' : 'JPEG'}</span>
                <span>
                  {photo.width}×{photo.height}
                </span>
                <span>{formatBytes(photo.sizeBytes)}</span>
                <span>EXIF ลบแล้ว</span>
              </div>
              {sizeAdvice(photo.sizeBytes, imageSettings) && (
                <p className="hint">{sizeAdvice(photo.sizeBytes, imageSettings)}</p>
              )}
            </>
          ) : (
            <div className="photo-frame">
              <span className="muted small">{photoBusy ? 'กำลังประมวลผลรูป…' : 'ยังไม่มีภาพ'}</span>
            </div>
          )}

          {photoError && (
            <p className="error-text" role="alert">
              {photoError}
            </p>
          )}

          <label htmlFor="photo-input" className="btn btn-secondary btn-block">
            {photo ? 'ถ่ายใหม่' : 'เปิดกล้องเพื่อถ่ายภาพ'}
          </label>
        </div>
      )}

      {/* ---- ขั้นที่ 4: ตรวจสอบ ---- */}
      {step === 'review' && gps.best && photo && (
        <div className="card stack">
          <div>
            <h1 className="card-title">{STEP_TITLES.review}</h1>
            <p className="card-sub">แก้ไขได้จนกว่าจะกดส่ง</p>
          </div>
          <div className="perf" />

          <dl className="kv">
            <div>
              <dt>เลขทะเบียน</dt>
              <dd>{registrationCheck.normalized}</dd>
            </div>
            <div>
              <dt>ละติจูด</dt>
              <dd>{gps.best.latitude.toFixed(6)}</dd>
            </div>
            <div>
              <dt>ลองจิจูด</dt>
              <dd>{gps.best.longitude.toFixed(6)}</dd>
            </div>
            <div>
              <dt>ความแม่นยำ</dt>
              <dd>{metres(gps.best.accuracyM)}</dd>
            </div>
            <div>
              <dt>ผู้บันทึก</dt>
              <dd>{session.user.displayName}</dd>
            </div>
            <div>
              <dt>ภาพ</dt>
              <dd>{formatBytes(photo.sizeBytes)}</dd>
            </div>
          </dl>

          <div className="photo-frame">
            <img src={photo.previewUrl} alt="ภาพที่จะบันทึก" />
          </div>

          {needsOverride && (
            <div className="banner banner-warn">
              <div>
                <b>รายการนี้จะรออนุมัติจากหัวหน้างาน</b>
                ความแม่นยำ {metres(gps.best.accuracyM)} ไม่ผ่านเกณฑ์{' '}
                {metres(gpsSettings.thresholdM)} ระบบบันทึกค่าจริงไว้พร้อมเหตุผลที่คุณกรอก
              </div>
            </div>
          )}

          <div className="field">
            <label className="label" htmlFor="note">
              หมายเหตุ (ถ้ามี)
            </label>
            <textarea
              id="note"
              className="input"
              value={note}
              onChange={(event) => setNote(event.target.value)}
              maxLength={1000}
            />
          </div>
        </div>
      )}

      {/* ---- แถบปุ่มล่างจอ ---- */}
      <div className="actionbar">
        {step === 'registration' && (
          <button
            type="button"
            className="btn btn-block"
            disabled={!registrationCheck.valid}
            onClick={() => {
              setStep('gps');
              gps.start();
            }}
          >
            ถัดไป · หาตำแหน่ง
          </button>
        )}

        {step === 'gps' && (
          <>
            <button
              type="button"
              className="btn btn-block"
              disabled={!gpsReady}
              onClick={() => setStep('photo')}
            >
              {gpsReady
                ? 'ใช้พิกัดนี้ แล้วไปถ่ายภาพ'
                : needsOverride
                  ? 'กรอกเหตุผลเพื่อขออนุมัติ'
                  : 'ยังใช้พิกัดนี้ไม่ได้'}
            </button>
            <button type="button" className="btn btn-secondary btn-block" onClick={gps.start}>
              {gps.phase === 'searching' ? 'เริ่มหาใหม่' : 'ค้นหาสัญญาณอีกครั้ง'}
            </button>
          </>
        )}

        {step === 'photo' && (
          <button
            type="button"
            className="btn btn-block"
            disabled={!photo || photoBusy}
            onClick={() => setStep('review')}
          >
            ใช้ภาพนี้
          </button>
        )}

        {step === 'review' && (
          <>
            <button
              type="button"
              className="btn btn-block"
              disabled={submitting || !fresh}
              onClick={() => void submit()}
            >
              {submitting ? 'กำลังบันทึก…' : 'ส่งข้อมูล'}
            </button>
            <button
              type="button"
              className="btn btn-secondary btn-block"
              onClick={() => setStep('photo')}
            >
              ย้อนกลับไปแก้ไข
            </button>
          </>
        )}
      </div>
    </div>
  );
}
